import axios, { AxiosInstance } from "axios";
import { MsGraphAuthService, RedirectInProgressError, AuthLoopError } from "./MsGraphAuthService";
import {
  parseSelectedProjectIds,
  parseSelectedTagIds,
} from "../util/filterHandler";

// Re-export for backward compatibility
export const LAST_LOGIN_REDIRECT = "lastLoginRedirect";

const authService = MsGraphAuthService.getInstance();

/**
 * Get authentication tokens using the centralized auth service
 * Uses token deduplication and proper caching
 */
async function getSilentToken(): Promise<
  { accessCode: string; idToken: string } | undefined
> {
  try {
    const token = await authService.getApiToken();
    if (token) {
      return { accessCode: token.accessToken, idToken: token.idToken };
    }
    return undefined;
  } catch (error) {
    // Let RedirectInProgressError and AuthLoopError propagate so callers can identify it
    if (error instanceof RedirectInProgressError || error instanceof AuthLoopError) {
      throw error;
    }
    console.error("[customAxios] Error while retrieving token:", error);
    return undefined;
  }
}

function getAxiosFactory(
  baseUrl: string,
  baseRoute: string,
): () => Promise<AxiosInstance> {
  return async () => {
    const token = await getSilentToken();
    if (!token) {
      throw new Error(
        "Failed to acquire authentication token. Please sign in again.",
      );
    }
    const axiosInstance = axios.create({
      baseURL: `${baseUrl}${baseRoute}`,
      headers: {
        Authorization: `Bearer ${token.accessCode}`,
        Identity: `${token.idToken}`,
        projectids: parseSelectedProjectIds().join(","),
        tagids: parseSelectedTagIds().join(","),
        "Content-Type": "application/json",
      },
    });

    // Add response interceptor for 401 handling
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // If the 401 is specifically about a revoked Google token, do NOT 
          // trigger MSAL re-authentication (the user's Pinnacle session is fine).
          if (error.response?.data?.code === "GOOGLE_TOKEN_REVOKED") {
            console.warn(
              "[customAxios] Ignoring 401 because it is a Google token revocation.",
            );
            return Promise.reject(error);
          }

          console.error(
            "[customAxios] 401 error, triggering re-authentication",
          );
          try {
            await authService.handle401Error();
          } catch (authError) {
            console.error("[customAxios] Failed to handle 401:", authError);
          }
        }
        return Promise.reject(error);
      },
    );

    return axiosInstance;
  };
}

export default getAxiosFactory;
